// LeaderboardCategory.jsx
import React from 'react';
import { useLoaderData } from 'react-router';
import { withScope, captureException } from '@sentry/react';

import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';

import { EMPTY_ARRAY } from '../resource/defaults.js';
import { DEFAULT_LANGUAGE, PREFERRED_LOCALES } from '../resource/i18nCommon.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';

/**
 * Generate title and description for leaderboard pages
 * @param {Object} params
 * @param {string} params.category - Leaderboard category
 * @param {string} params.range - Time range (24h, 7d, 30d)
 * @param {Object} params.i18n - i18n instance
 * @param {string} [params.language] - Language code (defaults to DEFAULT_LANGUAGE)
 * @returns {Object} Object with title and description
 */
const getLeaderboardMetaText = ({ category, range, i18n, language }) => {
  const lang = language || DEFAULT_LANGUAGE;
  const { t } = i18n;

  const categoryText = t(`leaderboard_${category}`, {
    lng: lang,
    ns: TranslationNamespace.FEED,
  });
  const rangeText = t(`leaderboard_range_${range}`, {
    lng: lang,
    ns: TranslationNamespace.FEED,
  });
  const homeTitle = t('seo-home-title', {
    lng: lang,
    ns: TranslationNamespace.SEO,
  });

  const title = `${categoryText} ${rangeText} - ${homeTitle}`;
  const description = `${categoryText} ${rangeText}`;

  return { title, description };
};

/**
 * Generate JSON-LD for leaderboard pages
 * @param {Object} params
 * @param {string} params.region - Leaderboard region
 * @param {string} params.category - Leaderboard category
 * @param {string} params.range - Time range (24h, 7d, 30d)
 * @param {string} params.origin - Origin URL (e.g., 'https://swag.live')
 * @param {Object} params.i18n - i18n instance
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes
 * @returns {Object} JSON-LD object with @context and @graph
 */
const generateLeaderboardJsonLd = async ({
  state,
  region,
  category,
  range,
  origin,
  i18n,
}) => {
  const currentLanguage = i18n.language;
  const supportedLanguages = getSupportedLanguages(state) || PREFERRED_LOCALES;
  const alternateLanguages = supportedLanguages.filter(
    lang => currentLanguage !== lang
  );

  // Load language resources if needed
  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.FEED)) {
      await i18n.reloadResources(lang, TranslationNamespace.FEED);
    }
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });
  await Promise.all(loadLanguagePromises);

  // Generate title and description for a specific language
  const getTitleByLang = lang => {
    const { title } = getLeaderboardMetaText({
      category,
      range,
      i18n,
      language: lang,
    });
    return title;
  };

  const getDescriptionByLang = lang => {
    const { description } = getLeaderboardMetaText({
      category,
      range,
      i18n,
      language: lang,
    });
    return description;
  };

  const url = `${origin}/leaderboard/${region}/${category}/${range}`;

  // WebPage schema
  const webPage = {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    inLanguage: currentLanguage,
    name: getTitleByLang(currentLanguage),
    description: getDescriptionByLang(currentLanguage),
    breadcrumb: {
      '@id': `${url}#breadcrumb`,
    },
    mainEntity: {
      '@id': `${url}#feed`,
    },
  };

  // BreadcrumbList schema
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: i18n.t('seo-home-title', {
          lng: currentLanguage,
          ns: TranslationNamespace.SEO,
        }),
        item: {
          '@id': `${origin}/#website`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: i18n.t('seo_video_title', {
          lng: currentLanguage,
          ns: TranslationNamespace.SEO,
        }),
        item: {
          '@id': `${origin}/video#webpage`,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: getTitleByLang(currentLanguage),
        item: {
          '@id': `${url}#webpage`,
        },
      },
    ],
  };

  // ItemList schema
  const itemList = {
    '@type': 'ItemList',
    '@id': `${url}#feed`,
    url,
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [webPage, breadcrumbList, itemList],
  };
};

/**
 * React Router loader for LeaderboardCategory
 * Generates meta tags and JSON-LD for leaderboard pages
 */
export const loader = async ({ params, request, i18nInstance, language }) => {
  try {
    const { region, category, range } = params;
    const { store } = await createInitialStoreWithRequest({ params, request });
    const state = store.getState();

    const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';

    // Generate page title and description
    const { title: pageTitle, description } = getLeaderboardMetaText({
      category,
      range,
      i18n: i18nInstance,
      language,
    });

    // Generate canonical
    const canonicalPath = `/leaderboard/${region}/${category}/${range}`;
    const { canonical } = generateCanonicalAndAlternates({
      canonicalPath,
      language,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;

    const jsonLd = await generateLeaderboardJsonLd({
      state,
      region,
      category,
      range,
      origin,
      i18n: i18nInstance,
    });

    // Generate meta tags
    const meta = getMetaTagsForHelmet({
      title: pageTitle,
      description,
      siteName: brandingName,
      hostname,
      url: canonical,
      alternates: {
        canonical,
      },
      jsonLd,
    });

    return {
      data: { store: {} },
      meta,
      staticContext: {
        requestHeaders: {
          hostname,
        },
      },
    };
  } catch (error) {
    withScope(scope => {
      scope.setExtra('params', params);
      captureException(error);
    });
    return {
      data: null,
      error,
      meta: EMPTY_ARRAY,
    };
  }
};

const LeaderboardCategoryRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'LeaderboardCategory',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalComponent
        filename="Container_LeaderboardCategory"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/LeaderboardCategory.js')
        }
      />
    </>
  );
};

export default LeaderboardCategoryRoute;
